import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Membership Committee review of upgrade applications.
 *   Admin      → assign committee members to an under-review application + view their recommendations.
 *   Member     → list applications assigned to me + submit approve/reject + remark.
 *   Chairman   → list all applications with every member's recommendation + final decision.
 */
export const committeeReviewApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ── Admin ─────────────────────────────────────────────────────────── */

    // Pool of Membership Committee members the admin can assign.
    committeeMembersPool: builder.query<
      { committee: { id: number; name: string } | null; members: any[] },
      void
    >({
      query: () => "/admin/upgrade-applications/committee/members",
    }),

    upgradeCommitteeReviews: builder.query<any[], string>({
      query: (id) => `/admin/upgrade-applications/${id}/committee/reviews`,
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: (_r, _e, id) => [{ type: "UpgradeCommitteeReviews" as const, id }],
    }),

    assignUpgradeCommittee: builder.mutation<any, { id: string; member_ids: string[] }>({
      query: ({ id, member_ids }) => ({
        url: `/admin/upgrade-applications/${id}/committee/assign`,
        method: "POST",
        body: { member_ids },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "UpgradeCommitteeReviews" as const, id }],
    }),

    unassignUpgradeCommittee: builder.mutation<any, { id: string; reviewId: number }>({
      query: ({ id, reviewId }) => ({
        url: `/admin/upgrade-applications/${id}/committee/reviews/${reviewId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "UpgradeCommitteeReviews" as const, id }],
    }),

    /* ── Committee member ──────────────────────────────────────────────── */

    myUpgradeReviews: builder.query<any[], void>({
      query: () => "/committee/my-upgrade-reviews",
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: [{ type: "MyUpgradeReviews" as const, id: "LIST" }],
    }),

    decideUpgradeReview: builder.mutation<
      any,
      { reviewId: number; decision: "approved" | "rejected"; remark?: string }
    >({
      query: ({ reviewId, ...body }) => ({
        url: `/committee/upgrade-reviews/${reviewId}/decide`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "MyUpgradeReviews" as const, id: "LIST" }],
    }),

    /* ── Chairman ──────────────────────────────────────────────────────── */

    chairmanUpgradeApplications: builder.query<any[], void>({
      query: () => "/committee/upgrade-applications",
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: [{ type: "CommitteeUpgradeApps" as const, id: "LIST" }],
    }),

    finalizeUpgradeApplication: builder.mutation<
      any,
      { id: string; decision: "approved" | "rejected"; remark?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/committee/upgrade-applications/${id}/finalize`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CommitteeUpgradeApps" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCommitteeMembersPoolQuery,
  useUpgradeCommitteeReviewsQuery,
  useAssignUpgradeCommitteeMutation,
  useUnassignUpgradeCommitteeMutation,
  useMyUpgradeReviewsQuery,
  useDecideUpgradeReviewMutation,
  useChairmanUpgradeApplicationsQuery,
  useFinalizeUpgradeApplicationMutation,
} = committeeReviewApi;
